import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../lib/api';
import { api } from '../lib/api';
import { AppShell } from '../components/Layout';
import { Banner, Field, Loading } from '../components/ui';
import { formatDateTime } from '../lib/format';

const TRUST_TONE: Record<string, string> = {
  applied: 'ok', passed: 'ok', not_applied: '', indeterminate: 'warn',
  invalid: 'danger', failed: 'danger', not_evaluated: '',
};

function TrustRow({ label, value, note }: { label: string; value: string; note?: string }) {
  const tone = TRUST_TONE[value] ?? '';
  return (
    <>
      <div className="kv__k">{label}</div>
      <div className="kv__v">
        <span className={`pill${tone ? ` pill--${tone}` : ''}`}>{value.replace(/_/g, ' ')}</span>
        {note && <div className="small muted" style={{ marginTop: 4 }}>{note}</div>}
      </div>
    </>
  );
}

export function Verify() {
  const [file, setFile] = useState<File | null>(null);
  const [envelopeId, setEnvelopeId] = useState('');
  const [result, setResult] = useState<any>(null);
  const [keyMaterial, setKeyMaterial] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const resultRef = useRef<HTMLDivElement>(null);

  useEffect(() => { api.get('/verify/key').then(setKeyMaterial).catch(() => undefined); }, []);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!file) return;
    setBusy(true); setError(null); setResult(null);
    try {
      const body = new FormData();
      body.append('file', file);
      if (envelopeId.trim()) body.append('envelope_id', envelopeId.trim());
      const data = await api.post('/verify/file', body);
      setResult(data);
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth' }), 60);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Verification failed.');
    } finally { setBusy(false); }
  };

  const matched = result?.exact_byte_match?.matched;

  return (
    <AppShell nav={[{ to: '/', label: 'Home' }, { to: '/pricing', label: 'Pricing' }]}>
      <div className="shell section stack-l">
        <header className="stack-s" style={{ maxWidth: '66ch' }}>
          <h1>Verify a signed document</h1>
          <p className="muted">
            Upload a PDF to compare its exact bytes against the recorded revisions
            of an agreement. Byte match, evidence validity and external trust are
            reported separately — none of them substitutes for the others.
          </p>
        </header>

        <div className="card stack">
          <form className="stack" onSubmit={submit}>
            <Field label="The PDF to check" id="verify-file" required>
              <input id="verify-file" className="input" type="file" required
                     accept="application/pdf,.pdf" style={{ paddingTop: 10 }}
                     onChange={(event) => setFile(event.target.files?.[0] || null)} />
            </Field>
            <Field label="Agreement ID" id="verify-env" required
                   hint="Printed on the completion report and in README.json inside the evidence export. A digest alone is never looked up across customers.">
              <input id="verify-env" className="input" placeholder="env_…"
                     value={envelopeId}
                     onChange={(event) => setEnvelopeId(event.target.value)} />
            </Field>
            <button className="btn btn--primary btn--large" disabled={busy || !file}>
              {busy ? 'Checking…' : 'Verify this document'}
            </button>
          </form>
        </div>

        {error && <Banner tone="danger" title="Could not verify">{error}</Banner>}
        {busy && <Loading label="Comparing bytes and checking evidence…" />}

        {result && (
          <div className="stack" ref={resultRef}>
            <Banner tone={matched ? 'ok' : 'danger'}
                    title={matched ? 'Exact byte match' : 'No matching revision'}>
              {result.exact_byte_match.message}
            </Banner>

            <div className="card stack-s">
              <h2 style={{ margin: 0, fontSize: '1.05rem' }}>The file you uploaded</h2>
              <div className="kv">
                <div className="kv__k">SHA-256</div>
                <div className="kv__v kv__v--mono">{result.supplied_file.digest}</div>
                <div className="kv__k">Size</div>
                <div className="kv__v">{result.supplied_file.byte_length.toLocaleString()} bytes</div>
              </div>
            </div>

            {result.envelope && (
              <div className="card stack-s">
                <h2 style={{ margin: 0, fontSize: '1.05rem' }}>Agreement</h2>
                <div className="kv">
                  <div className="kv__k">Title</div>
                  <div className="kv__v">{result.envelope.title}</div>
                  <div className="kv__k">Status</div>
                  <div className="kv__v">{result.envelope.status}</div>
                  <div className="kv__k">All signers finished</div>
                  <div className="kv__v">{formatDateTime(result.envelope.all_signed_at)}</div>
                  <div className="kv__k">Final document ready</div>
                  <div className="kv__v">{formatDateTime(result.envelope.completed_at)}</div>
                </div>
              </div>
            )}

            <div className="grid grid--two">
              <div className="card stack-s">
                <h2 style={{ margin: 0, fontSize: '1.05rem' }}>Evidence</h2>
                <div className="kv">
                  <TrustRow label="Manifest signature"
                            value={result.evidence.manifest.signature_valid ? 'passed' : 'failed'} />
                  <TrustRow label="Event chain"
                            value={result.evidence.event_chain.passed ? 'passed' : 'failed'} />
                  <div className="kv__k">Events recorded</div>
                  <div className="kv__v">{result.evidence.event_chain.event_count}</div>
                  <div className="kv__k">Evidence key</div>
                  <div className="kv__v kv__v--mono">{result.evidence.evidence_key.key_id}</div>
                </div>
                {result.evidence.artifact_findings.length > 0 && (
                  <Banner tone="danger" title="Artifact findings">
                    <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                      {result.evidence.artifact_findings.map((finding: any, index: number) => (
                        <li key={index}>{finding.message}</li>
                      ))}
                    </ul>
                  </Banner>
                )}
              </div>

              <div className="card stack-s">
                <h2 style={{ margin: 0, fontSize: '1.05rem' }}>External trust</h2>
                <div className="kv">
                  <TrustRow label="PDF seal" value={result.external_trust.pdf_seal} />
                  <TrustRow label="Certificate trust" value={result.external_trust.certificate_trust} />
                  <TrustRow label="Independent timestamp"
                            value={result.external_trust.independent_timestamp} />
                  <TrustRow label="Subsequent revisions"
                            value={result.external_trust.subsequent_revisions} />
                </div>
                <p className="small muted" style={{ margin: 0 }}>
                  {result.external_trust.explanation}
                </p>
              </div>
            </div>

            <div className="card card--pad-0">
              <div style={{ padding: '16px 18px 0' }}>
                <h2 style={{ margin: 0, fontSize: '1.05rem' }}>Recorded revisions</h2>
                <p className="card__sub">
                  Compare your file's digest with these. An authorized archival
                  revision legitimately produces different bytes.
                </p>
              </div>
              <div className="table-wrap" style={{ marginTop: 12 }}>
                <table className="table">
                  <thead><tr><th>Revision</th><th>SHA-256</th><th>Bytes</th><th></th></tr></thead>
                  <tbody>
                    {result.revision_chain.map((revision: any) => (
                      <tr key={revision.artifact_id}>
                        <td style={{ fontWeight: 540 }}>{revision.kind.replace(/_/g, ' ')}</td>
                        <td><span className="digest">{revision.digest}</span></td>
                        <td className="nowrap">{revision.byte_length.toLocaleString()}</td>
                        <td>
                          {revision.digest === result.supplied_file.digest && (
                            <span className="pill pill--ok">Your file</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <Banner tone="info" title="How to read this">
              {result.interpretation}
            </Banner>
          </div>
        )}

        {keyMaterial && (
          <div className="card stack-s">
            <h2 style={{ margin: 0, fontSize: '1.05rem' }}>Platform evidence key</h2>
            <p className="small muted" style={{ margin: 0 }}>{keyMaterial.notes}</p>
            <div className="kv">
              <div className="kv__k">Key ID</div>
              <div className="kv__v kv__v--mono">{keyMaterial.key_id}</div>
              <div className="kv__k">Algorithm</div>
              <div className="kv__v">{keyMaterial.algorithm}</div>
            </div>
            <details>
              <summary className="small" style={{ cursor: 'pointer' }}>
                Show the public key
              </summary>
              <pre className="codebox" style={{ marginTop: 10 }}>{keyMaterial.public_key_pem}</pre>
            </details>
            <p className="small muted" style={{ margin: 0 }}>
              {keyMaterial.obtain_independently}
            </p>
          </div>
        )}

        <p className="small muted">
          Looking for your own agreements? <Link to="/app">Open the dashboard</Link>.
        </p>
      </div>
    </AppShell>
  );
}
